import { format } from 'date-fns';
import { capitalize, find, includes, map, sortBy } from 'lodash-es';

import { showFlash } from '@/alerts';
import { dashboardBffClient } from '@/api-client';
import constants from '@/constants';
import { warn } from '@/logger';
import { defaultErrorHandler } from '@/services';
import { useAppInfoStore } from '@/stores/appInfo';
import { useMainStore } from '@/stores/main';
import { getFlattenedErrors } from '@/utils';
import { nth, pluralizeWord } from '@/utils/format-utils';
import { isProfileVBR } from '@/utils/profile-utils';

/**
 * Translate selectors
 */
export const selectors = {
  toObjects: function (selectorStrings) {
    let strings = selectorStrings;
    if (!Array.isArray(strings)) {
      strings = selectorStrings.split(',');
    }
    return strings.map((s) => {
      const keyVal = s.split(':');
      return { type: 'label', key: keyVal[0].trim(), value: keyVal[1].trim() };
    });
  },
  toStrings: function (selectorObjects) {
    return selectorObjects.map((o) => {
      if (o.key && o.value) return `${o.key}: ${o.value}`;
      return `namespace: ${o.namespace}`;
    });
  },
};

/**
 * Find policies associated with the given profile
 * @param profileName
 * @returns {Array}
 */
export function findAssociatedPolicyNames(profileName) {
  const store = useMainStore();
  return (
    store.policies
      ?.filter(
        (p) =>
          p.spec?.actions?.[1]?.exportParameters?.profile?.name === profileName ||
          p.spec?.actions?.[0]?.importParameters?.profile?.name === profileName ||
          p.spec?.actions?.[0]?.backupParameters?.profile?.name === profileName,
      )
      .map((profile) => profile.metadata.name) ?? []
  );
}

/**
 * Return the first policy found that covers cluster-scoped resources
 * @returns {Object} or undefined if not found
 */
export function findClusterScopedResourcesPolicy() {
  const store = useMainStore();
  return find(store.policies, (p) => {
    const coveredApps = p.spec?.selector?.matchExpressions?.[0]?.values;
    return includes(coveredApps, constants.NS_CLUSTER_RESOURCES);
  });
}

function handleErrorHelper(error = {}, objName = '') {
  const status = error.response?.status;

  warn(`Validation failed for new ${objName}. Showing validation flash msg.`, error);

  if (status === 409) {
    showFlash({
      message: `A ${objName} with this name already exists`,
      details: `Please change the ${objName} name and try again.`,
      messageType: 'warn',
      dismiss: 'user',
    });
  } else if (status === 400) {
    const errors = getFlattenedErrors(error.response?.data);
    const errorMessage =
      errors?.length > 0 ? errors[errors.length - 1] : constants.messages.SUPPORT;

    showFlash({
      message: `There was a problem validating the ${objName}`,
      details: errorMessage,
      messageType: 'warn',
      dismiss: 'user',
    });
  } else {
    defaultErrorHandler(error);
  }
}

export function handlePolicyError(error) {
  handleErrorHelper(error, 'policy');
}

export function handlePresetError(error) {
  handleErrorHelper(error, 'preset');
}

/**
 * Validates the given policy against the validation endpoint
 * @param policy
 * @return { valid: <Boolean>, message: <String> }
 */
export function validatePolicy(policy) {
  return dashboardBffClient.bff
    .bffValidatePolicyCr({ policy })
    .then(() => ({ valid: true, message: '' }))
    .catch((err) => {
      const status400 = err.response?.status === 400;
      const backendErrMsg = err.response?.data?.message;
      if (!status400) handlePolicyError(err);
      return { valid: false, message: backendErrMsg.replace('Bad Request. Cause: ', '') };
    });
}

/*
  ============= Resource Filter Functions =============
 */

export function summarizeResourceFilter(gvrnShow, gvrn, summaryStart) {
  const phrases = [];
  if (gvrnShow.g) {
    phrases.push(`<b>group is <kbd>${gvrn.group || '&hellip;'}</kbd></b>`);
  }
  if (gvrnShow.v) {
    phrases.push(`<b>version is <kbd>${gvrn.version || '&hellip;'}</kbd></b>`);
  }
  if (gvrnShow.r) {
    phrases.push(`<b>resource type is <kbd>${gvrn.resource || '&hellip;'}</kbd></b>`);
  }
  if (gvrnShow.n) {
    phrases.push(`<b>name is <kbd>${gvrn.name || '&hellip;'}</kbd></b>`);
  }
  if (gvrnShow.l) {
    let labels = '&hellip;';
    if (gvrn.label.length) {
      labels = map(gvrn.label, (l) => `<b><kbd>${l.label}</kbd></b>`).join(' and ');
    }
    phrases.push(`<b>labels include</b> ${labels}`);
  }
  if (!phrases.length) return '';
  return `${summaryStart} where ${phrases.join(' and ')}`;
}

/*
  ============= Snapshot summary functions =============
 */
const weekdayMapLong = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

const monthMapLong = {
  1: 'January',
  2: 'February',
  3: 'March',
  4: 'April',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'August',
  9: 'September',
  10: 'October',
  11: 'November',
  12: 'December',
};

export function formatTime(h, m) {
  const utcDisplay = new Date();
  utcDisplay.setHours(h, m, 0, 0);
  const localDisplay = new Date();
  localDisplay.setUTCHours(h, m, 0, 0);
  return `${format(utcDisplay, 'h:mmaaa')} UTC (${format(localDisplay, 'h:mmaaa')} local)`;
}
/**
 * Pad a number with leading zero
 * Ex: 1 -> 01, 10 -> 10
 * @param num
 * @returns {string|*}
 */
export function pad(num) {
  return `${num}`.padStart(2, 0);
}
export function formatMin(m) {
  return `<kbd>:${pad(m)}</kbd>`;
}
export function formatHour(h, m) {
  return `<kbd>${formatTime(h, m)}</kbd>`;
}
export function formatWeekday(d) {
  return `<kbd>${weekdayMapLong[d]}s</kbd>`;
}
export function formatDaysOfMonth(d) {
  return `<kbd>${nth(d)}</kbd>`;
}
export function formatMonth(m) {
  return `<kbd>${monthMapLong[m]}</kbd>`;
}
/**
 * Pad a number with leading zero
 * Ex: 1 -> 01, 10 -> 10
 * @param num
 * @returns {string|*}
 */
export function padView(num) {
  return `${num}`.padStart(2, 0);
}
export function formatMinView(m) {
  return `<span class="font-bold">:${padView(m)}</span>`;
}
export function formatHourView(h, m) {
  return `<span class="font-bold">${formatTime(h, m)}</span>`;
}
export function formatWeekdayView(d) {
  return `<span class="font-bold">${weekdayMapLong[d]}s</span>`;
}
export function formatDaysOfMonthView(d) {
  return `<span class="font-bold">${nth(d)}</span>`;
}
export function formatMonthView(m) {
  return `<span class="font-bold">${monthMapLong[m]}</span>`;
}

/**
 * Creates an array of the desired format within a given limit
 * @params n(limit), function(format)
 * @returns {array|*}
 */
export const times = (n, func = (i) => i) => Array.from({ length: n }).map((_, i) => func(i));

/**
 * Convert the policy action into text we can display
 * @param action
 * @return {string}
 */
export function actionDisplay(action) {
  let a = action || '';
  a = a.replace('backup', 'snapshot').replace('report', 'generate reports');
  return capitalize(a);
}

/**
 * Convert the policy frequency into text we can display
 * @param freq
 * @return {string}
 */
export function frequencyDisplay(freq) {
  let f = freq || '';
  f = f.replace('onDemand', 'on-demand');
  return f;
}

/**
 * Takes subFrequency object and create human-readable summary string
 * @param params {Object}
 * @returns {String}
 */
export function snapshotFrequencySummary(params) {
  const { action, frequency, subFrequency, showMoreFreqOptions, showBackupWindow, backupWindow } =
    params;
  const hour = subFrequency?.hours?.[0] || 0;
  const minute = subFrequency?.minutes?.[0] || 0;
  const day = subFrequency?.days?.[0] || 1;
  const startHour = backupWindow?.start?.hour;
  const startMin = backupWindow?.start?.minute;
  const endHour = backupWindow?.end?.hour;
  const endMin = backupWindow?.end?.minute;

  const act = `<b>${actionDisplay(action)}</b>`;

  switch (frequency) {
    case '@hourly': {
      if (showBackupWindow) {
        return `${act} from ${formatHour(startHour, startMin)} to ${formatHour(
          endHour,
          endMin,
        )} each hour`;
      }

      if (showMoreFreqOptions) {
        return `${act} at ${sortBy(subFrequency.minutes).map(formatMin).join(', ')} each hour`;
      }

      return `${act} at ${sortBy(subFreqDefaults().minutes).map(formatMin).join(', ')} each hour`;
    }
    case '@daily': {
      if (showBackupWindow) {
        return `${act} from ${formatHour(startHour, startMin)} to ${formatHour(
          endHour,
          endMin,
        )} each day`;
      }

      if (showMoreFreqOptions) {
        const hourList = sortBy(subFrequency.hours).map((h) => formatHour(h, minute));
        return `${act} at ${hourList.join(' and ')} each day`;
      }

      return `${act} at ${sortBy(subFreqDefaults().hours)
        .map((h) => formatHour(h, minute))
        .join(' and ')} each day`;
    }
    case '@weekly': {
      if (showBackupWindow) {
        return `${act} on ${sortBy(subFrequency?.weekdays || [0])
          .map(formatWeekday)
          .join(' and ')} from ${formatHour(startHour, startMin)} to ${formatHour(
          endHour,
          endMin,
        )}`;
      }

      if (showMoreFreqOptions) {
        return `${act} on ${sortBy(subFrequency.weekdays)
          .map(formatWeekday)
          .join(' and ')} at <kbd>${formatTime(hour, minute)}</kbd>`;
      }

      return `${act} on ${sortBy(subFreqDefaults().weekdays)
        .map(formatWeekday)
        .join(' and ')} at <kbd>${formatTime(hour, minute)}</kbd>`;
    }
    case '@monthly': {
      if (showBackupWindow) {
        return `${act} on the ${sortBy(subFrequency?.days || [1])
          .map(formatDaysOfMonth)
          .join(' and ')} day of the month from ${formatHour(startHour, startMin)} to ${formatHour(
          endHour,
          endMin,
        )}`;
      }

      if (showMoreFreqOptions) {
        return `${act} on the ${sortBy(subFrequency.days)
          .map(formatDaysOfMonth)
          .join(' and ')} day of the month at <kbd>${formatTime(hour, minute)}</kbd>`;
      }

      return `${act} on the ${sortBy(subFreqDefaults().days)
        .map(formatDaysOfMonth)
        .join(' and ')} day of the month at <kbd>${formatTime(hour, minute)}</kbd>`;
    }
    case '@yearly': {
      if (showBackupWindow) {
        return `${act} on the ${formatDaysOfMonth(day)} of ${sortBy(subFrequency?.months || [1])
          .map(formatMonth)
          .join(' and ')} from ${formatHour(startHour, startMin)} to ${formatHour(
          endHour,
          endMin,
        )}`;
      }

      if (showMoreFreqOptions) {
        return `${act} on the ${formatDaysOfMonth(day)} of ${sortBy(subFrequency.months)
          .map(formatMonth)
          .join(' and ')} at <kbd>${formatTime(hour, minute)}</kbd>`;
      }

      return `${act} on the ${formatDaysOfMonth(day)} of ${sortBy(subFreqDefaults().months)
        .map(formatMonth)
        .join(' and ')} at <kbd>${formatTime(hour, minute)}</kbd>`;
    }
    default: {
      return `Unknown frequency ${frequency}`;
    }
  }
}

export function policyViewSnapshotFrequencySummary(params) {
  const { frequency, subFrequency, backupWindow } = params;
  const hour = subFrequency?.hours?.[0] || 0;
  const minute = subFrequency?.minutes?.[0] || 0;
  const day = subFrequency?.days?.[0] || 1;
  const startHour = backupWindow?.start?.hour;
  const startMin = backupWindow?.start?.minute;
  const endHour = backupWindow?.end?.hour;
  const endMin = backupWindow?.end?.minute;

  // const act = `<b>${actionDisplay(action)}</b>`;

  const formatBackupWindow = (startHour, startMin, endHour, endMin) =>
    `<span class='text-gray-600 dark:text-gray-400'>From</span> ${formatHourView(startHour, startMin)} <span class='text-gray-600 dark:text-gray-400'> to </span> <span class='font-bold'>${formatHourView(endHour, endMin)}</span>`;

  const formatFrequency = (items, formatter, conjunction = 'and') =>
    sortBy(items)
      .map((item, index, array) => {
        const formattedItem = formatter(item);
        if (index < array.length - 1) {
          return conjunction === ','
            ? `${formattedItem.trim()}, `
            : `${formattedItem} <span class='text-gray-600 dark:text-gray-400'> ${conjunction} </span>`;
        }
        return formattedItem;
      })
      .join('');

  switch (frequency) {
    case 'Hourly': {
      return backupWindow
        ? `${formatBackupWindow(startHour, startMin, endHour, endMin)} <span class='font-bold'> each hour<span>`
        : `<span class='text-gray-600 dark:text-gray-400'>${frequency} at</span> ${formatFrequency(subFrequency?.minutes, formatMinView, ',')} <span class="text-gray-600 dark:text-gray-400">each hour</span>`;
    }
    case 'Daily': {
      return backupWindow
        ? `${formatBackupWindow(startHour, startMin, endHour, endMin)} <span class="text-gray-600 dark:text-gray-400">each day</span>`
        : `<span class='text-gray-600 dark:text-gray-400'>At </span> ${formatFrequency(subFrequency?.hours, (h) => formatHourView(h, minute))} <span class="text-gray-600 dark:text-gray-400">each day</span>`;
    }
    case 'Weekly': {
      return backupWindow
        ? `<span class='text-gray-600 dark:text-gray-400'>On </span> ${formatFrequency(subFrequency?.weekdays || [0], formatWeekdayView)} <span class="text-gray-600 dark:text-gray-400">from</span> <span class='font-bold'> ${formatHourView(startHour, startMin)} </span> <span class='text-gray-600 dark:text-gray-400'> to </span> <span class="font-bold"> ${formatHourView(endHour, endMin)}</span>`
        : `<span class='text-gray-600 dark:text-gray-400'>On </span>${formatFrequency(subFrequency?.weekdays || [0], formatWeekdayView)} <span class="text-gray-600 dark:text-gray-400">at</span> <span class='font-bold'>${formatTime(hour, minute)}</span>`;
    }
    case 'Monthly': {
      return backupWindow
        ? `<span class='text-gray-600 dark:text-gray-400'>On the </span>${formatFrequency(subFrequency?.days || [1], formatDaysOfMonthView)}<span class='text-gray-600 dark:text-gray-400'> day of the month from </span> ${formatHourView(startHour, startMin)} <span class="text-gray-600 dark:text-gray-400">to</span> <span class="font-bold">${formatHourView(endHour, endMin)}</span>`
        : `<span class='text-gray-600 dark:text-gray-400'>On the </span> ${formatFrequency(subFrequency?.days || [1], formatDaysOfMonthView)}<span class='text-gray-600 dark:text-gray-400'> day of the month at </span> <span class="font-bold">${formatTime(hour, minute)}</span>`;
    }
    case 'Yearly': {
      return backupWindow
        ? `<span class='text-gray-600 dark:text-gray-400'>On the </span>${formatDaysOfMonthView(day)}<span class='text-gray-600 dark:text-gray-400'> of </span>${formatFrequency(subFrequency?.months || [1], formatMonthView)} from ${formatHourView(startHour, startMin)} to ${formatHourView(endHour, endMin)}`
        : `<span class='text-gray-600 dark:text-gray-400'>On the </span>${formatDaysOfMonthView(day)}<span class='text-gray-600 dark:text-gray-400'> of </span>${formatFrequency(subFrequency?.months || [1], formatMonthView)}<span class='text-gray-600 dark:text-gray-400'> at </span><span class='font-bold'>${formatTime(hour, minute)}</span>`;
    }
    default: {
      return `<span class='text-gray-600 dark:text-gray-400'>${frequency || 'Unknown'}</span>`;
    }
  }
}

/**
 * Find profile references in a policy
 * @param policy {Object}
 * @return {[]} Array of strings (profile names)
 */
export function policyProfiles(policy) {
  const profileNames = policy.spec?.actions?.flatMap((action) => {
    return [
      action.backupParameters?.profile?.name,
      action.exportParameters?.blockModeProfile?.name,
      action.exportParameters?.profile?.name,
      action.importParameters?.profile?.name,
      action.restoreParameters?.artifactOverrideProfile?.name,
      action.restoreParameters?.profile?.name,
      action.upgradeParameters?.profile?.name,
    ];
  });

  return [...new Set(profileNames?.filter(Boolean))];
}

export function summarizePolicy(policy) {
  let action = policy?.spec?.actions?.[0]?.action;
  action = `${capitalize(action.replace('backup', 'snapshot'))}`;
  let actionSec = policy?.spec?.actions?.[1]?.action;
  if (actionSec) actionSec = `${actionSec.replace('backup', 'snapshot')}`;

  const profiles = policyProfiles(policy);

  let resp = action;
  if (actionSec) resp += ` + ${actionSec}`;
  resp += ' policy';
  if (profiles.length) {
    resp += ` depends on ${pluralizeWord(profiles.length, 'profile')} `;
    resp += `<b>${profiles.join(', ')}</b>.`;
  }
  return resp;
}

/**
 * We cannot submit a policy or profile unless we know the cluster Kasten namespace, which is
 * fetched from the BFF 'systemInfo' endpoint.  If for some reason this enppoint has not been
 * fetched, we must warn the user and stop policy/profile creation.
 *
 * @param objectType {String} - "profile" or "policy"
 * @return {boolean}
 */
export function validateSystemInfoExists(objectType) {
  const appInfoStore = useAppInfoStore();
  // clusterId is returned with systemInfo, otherwise is blank.
  if (appInfoStore.systemInfo?.clusterId) return true;
  showFlash({
    message: `Unable to validate ${objectType}`,
    details: `We're having trouble determining which namespace Kasten is running in, which prevents the ${objectType} from being created.  It may help to refresh the browser and try again.`,
    messageType: 'warn',
    icon: 'icon-info-circle',
    dismiss: 'user',
  });
  return false;
}

/**
 * This function triggers alerts when validateCustomLabel fails.
 */
export function triggerCustomLabelAlerts({ type, text }) {
  const invalidCharMsg = `
    <br>The name and value in a label selector must begin and end with letters or numbers.
    The middle characters may also include dot, dash, and underscore.
    See <a target="_blank" rel="noopener noreferrer" href="https://kubernetes.io/docs/concepts/overview/working-with-objects/labels/#syntax-and-character-set">Kubernetes docs</a>
    for further details.
  `;
  const alertUser = (detailMsg) => {
    showFlash({
      message: 'Invalid selector format',
      details: detailMsg,
      messageType: 'warn',
      dismiss: 10000,
    });
  };
  // Ensure basic 'key:value' (or 'key=value') format
  if (type === 'COLON') {
    alertUser(`
      Valid selector syntax is <b>key:value</b> (or <b>key=value</b>).
      It should exactly match the label of the objects you are targeting.
      For example, <i>app:vuejs</i> is a valid selector.
    `);
  }
  if (type === 'LABEL_NAME_KEY') {
    alertUser(`The name <b>"${text}"</b> has invalid characters. ${invalidCharMsg}`);
  }
  if (type === 'LABEL_NAME_VALUE') {
    alertUser(`The value <b>"${text}"</b> has invalid characters. ${invalidCharMsg}`);
  }
}

/**
 * Validate a user-created selector.
 * This function is passed to the <v-select> as a callback.
 * @param selector {String} the text the user typed in
 * @return {Object} or false to be passed as arg of validateCustomLabelAlerts
 */
export function customLabelIsInvalid(selector) {
  // Ensure basic 'key:value' or 'key=value' format
  if (!constants.regex.KEY_COLON_VALUE.test(selector)) {
    return { type: 'COLON', text: null };
  }
  // Split on the first `:` or `=` (Kubernetes label keys cannot contain either,
  // so this is unambiguous).
  const separatorIndex = selector.search(/[:=]/);
  if (separatorIndex >= 0) {
    const key = selector.slice(0, separatorIndex).trim();
    const value = selector.slice(separatorIndex + 1).trim();

    if (!constants.regex.K8S_LABEL_NAME_VALUE.test(key)) {
      return { type: 'LABEL_NAME_KEY', text: key };
    }
    if (!constants.regex.K8S_LABEL_NAME_VALUE.test(value)) {
      return { type: 'LABEL_NAME_VALUE', text: value };
    }
  }
  return false;
}

/**
 * Validate a user-created app selector.
 * This function is passed to the <v-select> as a callback.
 * @param appSelector {String} - the text the user typed in
 * @return {*} - a valid v-select option
 */
export function validateCustomAppSelector(appSelector) {
  // this function must be side-effect-free.  vue-select calls createOption
  // inside the `filteredOptions` computed on every keystroke, so any reactive
  // writes (e.g. showFlash) here would cause infinite re-evaluation.
  if (!constants.regex.APP_SELECTOR.test(appSelector)) {
    return { label: appSelector, invalid: true };
  }

  const excluded = appSelector.startsWith('!');
  const namespace = excluded ? appSelector.slice(1) : appSelector;
  const option = { label: appSelector, namespace, type: 'namespace' };
  if (excluded) option.excluded = true;
  return option;
}

/**
 * Show a flash alert when the user commits an invalid app selector.
 * Intended to be used as an @option:created handler on v-select.
 */
export function alertInvalidAppSelector(option) {
  if (!option.invalid) return;
  showFlash({
    message: 'Invalid selector format',
    details:
      'The application selector must exactly match the application (namespace) name or contain a valid wildcard. Prefix with ! to exclude (e.g., !foo or !foo*).',
    messageType: 'warn',
    dismiss: 10000,
  });
}

/**
 * @typedef {Object} BlockModeExport
 * @property {boolean} [show]
 * @property {boolean} [showVbr]
 * @property {boolean} [required]
 * @property {Object|null} [blockModeProfile]
 */

/**
 * Derive the block-mode export state from the selected primary export profile.
 * @param {Object|null} newSelectedExportProfile
 * @param {BlockModeExport} [currentBlockMode]
 * @returns {BlockModeExport}
 */
export function createBlockModeProfileFromProfile(newSelectedExportProfile, currentBlockMode = {}) {
  if (!newSelectedExportProfile) {
    // K10-38112: clearing the primary profile after it was VBR-forced must also turn
    // off block mode, mirroring the VBR → non-VBR transition handled below.
    if (currentBlockMode.required) {
      return {
        ...currentBlockMode,
        show: false,
        blockModeProfile: null,
        showVbr: true,
        required: false,
      };
    }
    return { ...currentBlockMode, showVbr: true, required: false };
  }

  const newBlockModeProfileExport = { ...currentBlockMode };

  if (isProfileVBR(newSelectedExportProfile)) {
    newBlockModeProfileExport.blockModeProfile = newSelectedExportProfile;
    newBlockModeProfileExport.show = true;
    newBlockModeProfileExport.showVbr = false;
    newBlockModeProfileExport.required = true;

    return newBlockModeProfileExport;
  }

  newBlockModeProfileExport.showVbr = true;
  newBlockModeProfileExport.required = false;

  // K10-38112: when the primary export profile is switched from a VBR profile to a
  // non-VBR (e.g. Kopia) one, clear the VBR block-mode profile and turn off the
  // "Export volume snapshot data to VBR" toggle. `currentBlockMode.required` is only
  // ever set while the primary export profile is VBR, so it reliably identifies the
  // VBR → non-VBR transition. Leaving the toggle enabled would send only volume data
  // to VBR (the pre-9.0 split-metadata config); clearing it keeps all backup data together.
  if (currentBlockMode.required) {
    newBlockModeProfileExport.show = false;
    newBlockModeProfileExport.blockModeProfile = null;
  }

  return newBlockModeProfileExport;
}

export function policyByName(name) {
  const store = useMainStore();
  return store.policies?.find((p) => p.metadata.name === name);
}

export function subFreqDefaults() {
  return { minutes: [0], hours: [0], weekdays: [0], days: [1], months: [1] };
}

/**
 * Flags 'subfreq' when any sub-frequency array is empty; also clears a previous flag,
 * unlike validation.assert alone, so it can re-run outside validation.reset() cycles (K10-38161).
 * @param subFrequency - map of dimension name to selected values, e.g. { hours: [0, 6] }
 * @param validation - the form's FormValidation instance
 */
export function validateSubFrequency(subFrequency, validation) {
  const empty = Object.values(subFrequency).some((v) => !v.length);
  validation.fields.subfreq = { valid: true, message: '' };
  validation.assert('subfreq', !empty, 'At least one time must be selected.');
}

export function backupWindowDefaults() {
  return { start: { hour: 0, minute: 0 }, end: { hour: 2, minute: 0 } };
}

/**
 * Returns a integer ranking of the frequency.
 * Helpful for comparisons.
 * @param frequency
 * @return Number
 */
export function freqToInt(frequency) {
  const retentionMap = {
    hourly: 0,
    daily: 1,
    weekly: 2,
    monthly: 3,
    yearly: 4,
  };
  return frequency.startsWith('@') ? retentionMap[frequency.slice(1)] : retentionMap[frequency];
}

/**
 * Returns a new object with frequency entries removed
 * that are "smaller" than the given frequency.
 */
export function retentionGreaterThan(freq, retention) {
  return Object.entries(retention).reduce((acc, [key, value]) => {
    if (freqToInt(key) >= freqToInt(freq)) {
      acc[key] = value;
    }
    return acc;
  }, {});
}

export function subFreqMinsOptions() {
  const mins = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
  return mins.map((i) => ({ label: `:${pad(i)}`, value: i }));
}

export function subFreqHoursUTCOptions() {
  return times(24, (i) => ({ label: `${pad(i)}`, value: i }));
}

export function subFreqHoursLocalOptions() {
  return times(24, (i) => {
    const d = new Date();
    d.setHours(i, 0, 0, 0);
    return { label: format(d, 'haaa'), value: i };
  });
}

export function subFreqDaysOfWeekOptions() {
  const weekdayMap = { 0: 'Sun', 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat' };
  return times(7, (i) => ({ label: `${weekdayMap[i]}`, value: i }));
}

export function subFreqDaysOfMonthOptions() {
  return times(31, (i) => ({ label: `${pad(i + 1)}`, value: i + 1 }));
}

export function subFreqMonthsOptions() {
  // prettier-ignore
  const monthMap = { 1: 'Jan', 2: 'Feb', 3: 'Mar', 4: 'Apr', 5: 'May', 6: 'Jun', 7: 'Jul', 8: 'Aug', 9: 'Sep', 10: 'Oct', 11: 'Nov', 12: 'Dec', };
  return times(12, (i) => ({ label: `${monthMap[i + 1]}`, value: i + 1 }));
}

export function subFreqHoursOptions(showUTC) {
  return showUTC ? subFreqHoursUTCOptions() : subFreqHoursLocalOptions();
}
